/*
 * 2026 WilderdijkDynamics.
 * SIMGASM Version 4.115. Progressive enhancement for linked-product swatches
 * inside Recently Viewed. Keep native product hrefs as the no-JavaScript fallback.
 * This is scoped to the separate Recently Viewed cards; no global product-grid
 * handlers, events, cart behavior or storage keys are changed.
 */
(() => {
  'use strict';
  if (window.__wdRecentlyViewedSwitch482) return;
  window.__wdRecentlyViewedSwitch482 = true;

  const sectionSelector = '.product-recently-viewed--section';
  const cardSelector = '.wd-r80-card[data-recently-viewed-card]';
  const swatchSelector = '.wd-r80-swatches a.wd-r80-swatch';
  const pending = new WeakMap();

  // 2026 WilderdijkDynamics. V4.115. Recently Viewed quick add must behave
  // like the rest of the storefront: add through Empire's ATC flyout instead
  // of allowing the native product form to navigate directly to /cart.
  let recentlyViewedAtcFlyout = null;

  const serializeAtcForm = form => {
    const payload = [];
    new FormData(form).forEach((value, name) => {
      payload.push({ name, value });
    });
    return payload;
  };

  document.addEventListener('submit', event => {
    const form = event.target.closest ? event.target.closest('[data-wd-rv-atc-form]') : null;
    if (!form || !form.closest(sectionSelector)) return;

    event.preventDefault();

    const button = form.querySelector('[data-wd-rv-atc-button], button[type="submit"]');
    if (!button || button.disabled || button.classList.contains('processing')) return;

    const formData = serializeAtcForm(form);
    const hasVariant = formData.some(entry => entry.name === 'id' && String(entry.value || '').trim() !== '');
    if (!hasVariant) return;

    if (recentlyViewedAtcFlyout && typeof recentlyViewedAtcFlyout.unload === 'function') {
      recentlyViewedAtcFlyout.unload();
      recentlyViewedAtcFlyout = null;
    }

    if (typeof window.AddToCartFlyout === 'function') {
      recentlyViewedAtcFlyout = new window.AddToCartFlyout(formData, {
        atcButton: button,
        settings: {
          moneyFormat: form.getAttribute('data-money-format') || null,
          cartRedirection: false
        }
      });
      return;
    }

    // Defensive fallback for an unusually late Empire load. Never redirect to
    // /cart; add through Shopify AJAX and refresh the page cart count instead.
    button.disabled = true;
    button.classList.add('processing');
    fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form),
      credentials: 'same-origin'
    })
      .then(response => response.json().then(data => {
        if (!response.ok) throw data;
        return fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { credentials: 'same-origin' })
          .then(cartResponse => cartResponse.json())
          .then(cart => {
            window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
            document.dispatchEvent(new CustomEvent('addToCartFlyout:complete', {
              detail: { responseData: { ...data, items: [data] }, success: true }
            }));
          });
      }))
      .catch(error => {
        console.error('Recently viewed add to cart failed', error);
      })
      .finally(() => {
        button.disabled = false;
        button.classList.remove('processing');
      });
  });

  const normalizedPath = value => {
    try {
      return new URL(value, window.location.href).pathname.replace(/\/$/, '');
    } catch (_) {
      return '';
    }
  };

  document.addEventListener('click', async event => {
    const swatch = event.target.closest(swatchSelector);
    if (!swatch) return;
    const section = swatch.closest(sectionSelector);
    const card = swatch.closest(cardSelector);
    if (!section || !card || event.defaultPrevented) return;
    // Preserve normal browser actions, including open-in-new-tab and downloads.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
    if (swatch.classList.contains('is-active')) {
      event.preventDefault();
      return;
    }
    const destination = new URL(swatch.href, window.location.href);
    if (destination.origin !== window.location.origin || !destination.pathname.includes('/products/')) return;
    event.preventDefault();

    // A subsequent click cancels a previous request for this one card only.
    const oldRequest = pending.get(card);
    if (oldRequest) oldRequest.abort();
    const controller = new AbortController();
    pending.set(card, controller);
    card.setAttribute('aria-busy', 'true');
    const oldSwatches = card.querySelector('.wd-r80-swatches');
    const swatches = oldSwatches ? oldSwatches.cloneNode(true) : null;

    try {
      const requestUrl = new URL(destination.href);
      requestUrl.searchParams.set('view', 'recently-viewed');
      const response = await fetch(requestUrl.href, {
        method: 'GET', credentials: 'same-origin',
        headers: { Accept: 'text/html' }, signal: controller.signal
      });
      if (!response.ok) throw new Error('Product view unavailable');
      const responseText = await response.text();
      if (controller.signal.aborted) return;
      const doc = new DOMParser().parseFromString(responseText, 'text/html');
      const nextCard = doc.querySelector(cardSelector);
      const newMedia = nextCard?.querySelector('.wd-r80-media');
      const newBody = nextCard?.querySelector('.wd-r80-card__body');
      const oldMedia = card.querySelector('.wd-r80-media');
      const oldBody = card.querySelector('.wd-r80-card__body');
      if (!newMedia || !newBody || !oldMedia || !oldBody) throw new Error('Invalid product view');

      // Keep the original swatch family even if a linked product has no reciprocal
      // linked_colors metafield. Its genuine product URL remains available on every swatch.
      if (swatches) {
        swatches.querySelectorAll('a.wd-r80-swatch').forEach(link => {
          const selected = normalizedPath(link.href) === normalizedPath(destination.href);
          link.classList.toggle('is-active', selected);
          if (selected) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });
        const replacementSwatches = newBody.querySelector('.wd-r80-swatches');
        if (replacementSwatches) replacementSwatches.replaceWith(swatches);
        else {
          const slot = newBody.querySelector('.wd-r82-swatch-slot');
          if (slot) slot.append(swatches);
          else newBody.prepend(swatches);
        }
      }
      oldMedia.replaceWith(document.importNode(newMedia, true));
      oldBody.replaceWith(document.importNode(newBody, true));
      // Do not replace the article or the original remove control. The entry in
      // Empire's Recently Viewed list is still the product originally viewed.
      // The fetched card supplies real prices, image, stock, reviews and cart ID.
    } catch (error) {
      if (error.name !== 'AbortError' && !controller.signal.aborted) {
        // A real product link always works, even when an AJAX view cannot load.
        window.location.assign(destination.href);
      }
    } finally {
      if (pending.get(card) === controller) {
        pending.delete(card);
        card.removeAttribute('aria-busy');
      }
    }
  });
})();
